import { c as _c } from "react/compiler-runtime";
// A markdown body, drawn from rigid-core's document tree (Decision markdown-in-rigid-core): the client core parses,
// this only maps blocks and inlines to elements. No HTML is ever parsed or injected here. A link to an app path
// ("/chats?id=…") opens in the app, other links in a new tab; graph references (`ref`: a record id or `ns:Kind/name`)
// go to `onRef`, else open the record here (?id=). Styles: book.css `.md`.
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import type { MdBlock, MdDoc, MdInline } from "@/api";
type Props = {
  doc: MdDoc;
  onRef?: (ref: string) => void;
};
/** where a click goes: an app path, or a reference */
type Go = {
  path: (to: string) => void;
  ref: (ref: string) => void;
};
export function Markdown(t0) {
  const $ = _c(15);
  const {
    doc,
    onRef
  } = t0;
  const navigate = useNavigate();
  let t1;
  if ($[0] !== navigate) {
    t1 = to => navigate(to);
    $[0] = navigate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== navigate || $[3] !== onRef) {
    t2 = onRef ?? (r => navigate({
      search: `?id=${encodeURIComponent(r)}`
    }));
    $[2] = navigate;
    $[3] = onRef;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  let t4;
  let t5;
  if ($[5] !== doc.blocks || $[6] !== t1 || $[7] !== t2) {
    const go = {
      path: t1,
      ref: t2
    };
    t3 = "md";
    t4 = "markdown";
    t5 = blocks(doc.blocks, go);
    $[5] = doc.blocks;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className={t3} data-t={t4}>{t5}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function blocks(bs: MdBlock[], go: Go): ReactNode[] {
  return bs.map((b, i) => {
    switch (b.type) {
      case "heading":
        {
          const H = `h${Math.min(Math.max(b.level, 1), 6)}` as "h1";
          return <H key={i}>{inlines(b.inlines, go)}</H>;
        }
      case "paragraph":
        return <p key={i}>{inlines(b.inlines, go)}</p>;
      case "list":
        {
          const items = b.items.map((it, j) => <li key={j} data-task={it.task === undefined ? undefined : it.task ? "done" : "open"}>
						{it.task !== undefined && <input type="checkbox" checked={it.task} readOnly disabled aria-hidden />}
						{blocks(it.blocks, go)}
					</li>);
          return b.ordered ? <ol key={i} start={b.start}>{items}</ol> : <ul key={i}>{items}</ul>;
        }
      case "code":
        return <pre key={i} className="mono" data-lang={b.lang}>
						<code>{b.text}</code>
					</pre>;
      case "quote":
        return <blockquote key={i}>{blocks(b.blocks, go)}</blockquote>;
      case "rule":
        return <hr key={i} />;
    }
  });
}

/** A plain click (no modifier: those keep the browser's new tab or window). */
const plain = (e: React.MouseEvent) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button);
function inlines(xs: MdInline[], go: Go): ReactNode[] {
  return xs.map((x, i) => {
    switch (x.type) {
      case "text":
        return x.text;
      case "code":
        return <code key={i} className="mono">{x.text}</code>;
      case "em":
        return <em key={i}>{inlines(x.inlines, go)}</em>;
      case "strong":
        return <strong key={i}>{inlines(x.inlines, go)}</strong>;
      case "break":
        return <br key={i} />;
      case "link":
        {
          const app = x.href && /^\/(?!\/)/.test(x.href) ? x.href : null;
          if (x.href && !app) return <a key={i} href={x.href} target="_blank" rel="noreferrer noopener">
							{inlines(x.inlines, go)}
						</a>;
          return <a key={i} href={app ?? `?id=${encodeURIComponent(x.ref ?? "")}`} data-ref={x.ref} onClick={e => {
            if (!plain(e) || !app && !x.ref) return;
            e.preventDefault();
            if (app) go.path(app);else go.ref(x.ref!);
          }}>
						{inlines(x.inlines, go)}
					</a>;
        }
    }
  });
}